import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
} from "firebase/storage";
import { getFirebaseStorage } from "./firebase-ultra-safe";

// Storage 인스턴스 가져오기
const storage = getFirebaseStorage();

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// 이미지 업로드
export async function uploadImage(
  file: File,
  path: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  try {
    // Storage 초기화 확인
    if (!storage) {
      console.error("❌ Firebase Storage가 초기화되지 않았습니다.");
      return {
        success: false,
        error: "Storage 서비스를 사용할 수 없습니다.",
      };
    }

    // 파일 타입 검증
    if (!file.type.startsWith("image/")) {
      return {
        success: false,
        error: "이미지 파일만 업로드 가능합니다.",
      };
    }

    // 파일 크기 검증 (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        success: false,
        error: "파일 크기는 5MB를 초과할 수 없습니다.",
      };
    }

    // Firebase Storage 참조 생성
    const storageRef = ref(storage, path);

    // 업로드 실행 (진행률 추적 포함)
    const uploadTask = uploadBytesResumable(storageRef, file);

    // 진행률 콜백이 있다면 실행
    if (onProgress) {
      uploadTask.on("state_changed", snapshot => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress({
          loaded: snapshot.bytesTransferred,
          total: snapshot.totalBytes,
          percentage: progress,
        });
      });
    }

    // 업로드 실행
    const snapshot = await uploadTask;

    // 다운로드 URL 가져오기
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      url: downloadURL,
    };
  } catch (error) {
    console.error("이미지 업로드 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "이미지 업로드에 실패했습니다.",
    };
  }
}

// 여러 이미지 업로드
export async function uploadMultipleImages(
  files: File[],
  basePath: string,
  onProgress?: (index: number, progress: UploadProgress) => void
): Promise<{ success: boolean; urls: string[]; errors: string[] }> {
  const results = await Promise.allSettled(
    files.map((file, index) => {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${index}_${file.name}`;
      const path = `${basePath}/${fileName}`;

      return uploadImage(file, path, progress => {
        onProgress?.(index, progress);
      });
    })
  );

  const urls: string[] = [];
  const errors: string[] = [];

  results.forEach((result, index) => {
    if (result.status === "fulfilled" && result.value.success) {
      urls.push(result.value.url!);
    } else {
      const error =
        result.status === "rejected"
          ? result.reason
          : result.value.error || "업로드 실패";
      errors.push(`이미지 ${index + 1}: ${error}`);
    }
  });

  return {
    success: urls.length > 0,
    urls,
    errors,
  };
}

// 이미지 삭제
export async function deleteImage(
  url: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const imageRef = ref(storage, url);
    await deleteObject(imageRef);
    return { success: true };
  } catch (error) {
    console.error("이미지 삭제 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "이미지 삭제에 실패했습니다.",
    };
  }
}

// 판매글 이미지 업로드 (여러 이미지) - 서버 사이드 API 사용
export async function uploadImages(
  files: File[],
  onProgress?: (index: number, progress: UploadProgress) => void
): Promise<{ success: boolean; urls: string[]; errors: string[] }> {
  try {
    console.log("🔍 이미지 업로드 시작");

    // Firebase Auth ID 토큰 가져오기 (NextAuth 세션이 없을 경우 대비)
    let authToken: string | null = null;
    try {
      const { getFirebaseAuth } = await import("./firebase-ultra-safe");
      const auth = getFirebaseAuth();
      const currentUser = auth?.currentUser;
      if (currentUser) {
        authToken = await currentUser.getIdToken();
        console.log("✅ Firebase ID 토큰 가져오기 성공");
      }
    } catch (tokenError) {
      console.warn("⚠️ Firebase ID 토큰 가져오기 실패 (NextAuth 세션 사용):", tokenError);
    }

    // 서버 사이드 API로 업로드 (CORS 문제 해결)
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    // 진행률 시뮬레이션 (서버 사이드에서는 실제 진행률을 알 수 없음)
    if (onProgress) {
      files.forEach((_, index) => {
        // 즉시 50% 진행률 표시
        setTimeout(() => {
          onProgress(index, {
            loaded: 0,
            total: 0,
            percentage: 50,
          });
        }, 0);
      });
    }
    
    // 헤더 구성
    const headers: HeadersInit = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch('/api/product/upload-images', {
      method: 'POST',
      credentials: 'include', // 쿠키 포함하여 세션 전달
      headers,
      body: formData,
    });
    
    console.log("🔍 이미지 업로드 응답 상태:", response.status);
    console.log("🔍 응답 헤더:", Object.fromEntries(response.headers.entries()));

    const result = await response.json();

    if (!response.ok) {
      console.error("❌ 이미지 업로드 API 실패:", result);
      return {
        success: false,
        urls: [],
        errors: result.errors || [result.error || "이미지 업로드에 실패했습니다."],
      };
    }

    // 진행률 완료 표시
    if (onProgress) {
      files.forEach((_, index) => {
        setTimeout(() => {
          onProgress(index, {
            loaded: 100,
            total: 100,
            percentage: 100,
          });
        }, 100);
      });
    }

    return {
      success: result.success,
      urls: result.urls || [],
      errors: result.errors || [],
    };
  } catch (error: any) {
    console.error("❌ 이미지 업로드 실패:", error);
    return {
      success: false,
      urls: [],
      errors: [error?.message || "이미지 업로드에 실패했습니다."],
    };
  }
}

// 이미지 URL에서 파일명 추출
export function getFileNameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.split("/").pop() || "";
  } catch {
    return "";
  }
}
